import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  InputBase,
} from "@mui/material";
import ChecklistCard from "../../components/checklists/ChecklistCard";
import AddChecklistDrawer from "../../components/checklists/AddChecklistDrawer";
import EditChecklistDrawer from "../../components/checklists/EditChecklistDrawer";
import { Icon } from "../../icons";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";

const ChecklistsPage = () => {
  const { userData, token } = useAuth();
  
  // Get user role from userData
  const userRole = userData?.role || "CLIENT"; // Default to CLIENT if not found
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalChecklists, setTotalChecklists] = useState(0);
  const [pageSize] = useState(20); // Limit to 20 items per page
  
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch checklist templates from API
  useEffect(() => {
    fetchChecklists();
  }, [page, searchDebounce]);

  const fetchChecklists = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: page,
        limit: pageSize,
      };

      // Add search query if exists
      if (searchDebounce.trim()) {
        params.search = searchDebounce.trim();
      }

      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(`${url}/api/admin/checklist-templates?${new URLSearchParams(params)}`, {
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch checklists: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.data && Array.isArray(data.data)) {
        const formattedChecklists = data.data.map((template, index) => ({
          id: template._id || `checklist-${index + 1}`,
          title: template.title || "Untitled Checklist",
          createdBy: template.createdBy?.name || template.createdBy?.email || "Admin",
          createdDate: template.createdAt 
            ? new Date(template.createdAt).toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/')
            : "Unknown date",
          completedCount: template.completedRequests || 0,
          requestsCount: template.noOfRequests || 0,
          templateData: template
        }));
        
        setChecklists(formattedChecklists);
        
        // Set pagination data
        setTotalChecklists(data.total || data.data.length);
        setTotalPages(data.totalPages || Math.ceil((data.total || data.data.length) / pageSize));
      } else {
        setChecklists([]);
        setTotalChecklists(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching checklists:", error);
      setError("Failed to load checklists. Please try again.");
      setChecklists([]);
      setTotalChecklists(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Handle save checklist
  const handleSaveChecklist = (newChecklist) => {
    fetchChecklists();
    setSnackbar({
      open: true,
      message: "Checklist created successfully!",
      severity: "success",
    });
  };

  // Handle edit button click - Only if user is admin
  const handleEditChecklist = (checklistData) => {
    // Check if user is admin before allowing edit
    const isAdmin = userRole === "ADMIN" || userRole === "SUPERADMIN";
    
    if (!isAdmin) {
      setSnackbar({
        open: true,
        message: "You don't have permission to edit checklists",
        severity: "warning",
      });
      return;
    }
    
    console.log("Editing checklist:", checklistData);
    setSelectedChecklist(checklistData);
    setIsEditDrawerOpen(true);
  };

  // Handle update checklist
  const handleUpdateChecklist = (updatedChecklist) => {
    setChecklists(prevChecklists => 
      prevChecklists.map(item => 
        item.id === updatedChecklist._id 
          ? { 
              ...item, 
              title: updatedChecklist.title,
              templateData: updatedChecklist,
              createdDate: updatedChecklist.updatedAt 
                ? new Date(updatedChecklist.updatedAt).toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/')
                : item.createdDate
            } 
          : item
      )
    );
    
    setSnackbar({
      open: true,
      message: "Checklist updated successfully!",
      severity: "success",
    });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Calculate displayed range
  const startIndex = totalChecklists > 0 ? ((page - 1) * pageSize) + 1 : 0;
  const endIndex = Math.min(page * pageSize, totalChecklists);

  // Check if user can add checklists (admin only)
  const canAddChecklist = userRole === "ADMIN" || userRole === "SUPERADMIN";

  return (
    <Box>
      {/* ================= PAGE HEADER ================= */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.H2,
            color: COLORS.PRIMARY,
          }}
        >
          Check Lists
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* ================= SEARCH ================= */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexGrow: 0,
              width: { xs: 180, sm: 250, md: 280 },
              px: 2,
              py: 0.7,
              borderRadius: 1.5,
              border: `1px solid ${COLORS.PRIMARY}`,
              backgroundColor: COLORS.BACKGROUND,
              color: COLORS.PRIMARY,
            }}
          >
            <Icon name="search" sx={{ fontSize: 18, color: "currentColor" }} />
            <InputBase
              placeholder="Search by Name"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1); // Reset to first page when searching
              }}
              sx={{
                flex: 1,
                fontFamily: FONT_FAMILY,
                ...TYPOGRAPHY.BODY_STANDARD,
                color: COLORS.TEXT,
                ml: 1,
              }}
            />
            {searchQuery && (
              <IconButton
                size="small"
                onClick={() => {
                  setSearchQuery("");
                  setPage(1);
                }}
                sx={{ padding: 0.5 }}
              >
                <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
              </IconButton>
            )}
          </Box>

          {/* ================= ADD BUTTON - Only for Admin ================= */}
          {canAddChecklist && (
            <Button
              onClick={() => setIsAddDrawerOpen(true)}
              variant="contained"
              startIcon={<Icon name="plus2" />}
              sx={{
                backgroundColor: COLORS.SECONDARY,
                "&:hover": { backgroundColor: COLORS.SECONDARY },
                fontFamily: FONT_FAMILY,
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
              }}
            >
              Add New CheckList
            </Button>
          )}
        </Box>
      </Box>

      {/* ================= LOADING & ERROR STATES ================= */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: COLORS.SECONDARY }} />
        </Box>
      )}

      {error && !loading && (
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && checklists.length === 0 && (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              ...TYPOGRAPHY.BODY_LARGE,
              color: COLORS.MUTED_TEXT,
            }}
          >
            {searchDebounce ? "No checklists found matching your search." : "No checklists found. Create your first checklist!"}
          </Typography>
        </Box>
      )}

      {/* ================= CHECKLIST CARDS ================= */}
      {!loading && !error && checklists.length > 0 && (
        <>
<Box
  sx={{
    display: "flex",
    flexWrap: "wrap",
    gap: 3,
    "& > *": {
      // Keep everything the same, just change these 3 lines:
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: {
        xs: "100%",
        sm: "calc(50% - 12px)",
        md: "calc(33.333% - 16px)",
        lg: "calc(25% - 18px)",
      },
      // Keep your original maxWidth:
      maxWidth: {
        xs: "100%",
        sm: "calc(50% - 12px)",
        md: "calc(33.333% - 16px)",
        lg: "calc(25% - 18px)",
      },
    },
  }}
>
            {checklists.map((checklist) => (
              <Box
                key={checklist.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ChecklistCard
                  title={checklist.title}
                  createdBy={checklist.createdBy}
                  createdDate={checklist.createdDate}
                  completedCount={checklist.completedCount}
                  requestsCount={checklist.requestsCount}
                  onEdit={() => handleEditChecklist(checklist.templateData)}
                  userRole={userRole} // Pass user role to card
                />
              </Box>
            ))}
          </Box>

          {/* ================= PAGINATION ================= */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                p: 2,
                mt: 4,
                width: '100%',
                overflowX: 'auto',
              }}
            >
              <Typography sx={{ fontSize: 13, color: "#777", whiteSpace: 'nowrap' }}>
                {startIndex} - {endIndex} of {totalChecklists} checklists
              </Typography>

              <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
                <IconButton
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  size="small"
                  sx={{ padding: '4px' }}
                >
                  <Icon name="chevron-left" size={18} />
                </IconButton>

                {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (page === 1) {
                    pageNum = i + 1;
                  } else if (page === totalPages) {
                    pageNum = totalPages - 2 + i;
                  } else {
                    pageNum = page - 1 + i;
                  }

                  if (pageNum > 0 && pageNum <= totalPages) {
                    return (
                      <Button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        sx={{
                          minWidth: 24,
                          height: 24,
                          fontSize: "0.7rem",
                          bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                          color: page === pageNum ? "#fff" : COLORS.TEXT,
                          border: page === pageNum ? "none" : "1px solid #E1BEE7",
                          "&:hover": {
                            bgcolor: page === pageNum ? COLORS.PRIMARY : "rgba(0,0,0,0.04)"
                          },
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  }
                  return null;
                })}

                <IconButton
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  size="small"
                  sx={{ padding: '4px' }}
                >
                  <Icon name="chevron-right" size={18} />
                </IconButton>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* ================= ADD DRAWER - Only for Admin ================= */}
      {canAddChecklist && (
        <AddChecklistDrawer
          open={isAddDrawerOpen}
          onClose={() => setIsAddDrawerOpen(false)}
          onSave={handleSaveChecklist}
        />
      )}

      {/* ================= EDIT DRAWER - Only for Admin ================= */}
      <EditChecklistDrawer
        open={isEditDrawerOpen}
        onClose={() => {
          setIsEditDrawerOpen(false);
          setSelectedChecklist(null);
        }}
        checklistData={selectedChecklist}
        onUpdate={handleUpdateChecklist}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChecklistsPage;